/**
 * People who have asked to join, and where each one has got stuck.
 *
 * "Add a member, staff can add a prospective member to the board for
 * approval."
 *
 * ---------------------------------------------------------------------
 * THE COLUMN THAT MATTERS IS "WAITING ON"
 *
 * What goes wrong with a club's admissions process is almost never a
 * wrong decision. It is an application that sat for five months because
 * it needed a seconder and nobody looked at it again — and a list sorted
 * by status hides exactly that, because "submitted" looks like progress.
 *
 * So the list is sorted by how long each one has been waiting, longest
 * first, and every row says what is stopping it. The database computes
 * that (`v_membership_application.waiting_on`, migration 0193) rather
 * than this screen, because the answer changes as fields are filled in
 * and a copy here would drift.
 *
 * ---------------------------------------------------------------------
 * APPROVING IS NOT A BUTTON
 *
 * An approval needs a Board motion that actually carried. The database
 * refuses one without it (MEM-21), and this screen does not try to make
 * that easier — it asks which motion, because the answer is the point.
 * A club that can mark somebody approved without a vote has an
 * admissions process in name only.
 */

import { Fragment, useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { date, money } from '../lib/format.ts';
import { Card, Empty, ErrorBanner, Loading, Status, Tile } from '../components/ui.tsx';
import { PersonLookup, type PersonHit } from '../components/Lookup.tsx';
import { useSort, SortTh } from '../lib/useSort.tsx';

interface Application {
  id: string;
  person_id: string;
  applicant: string;
  applicant_email: string | null;
  applicant_phone: string | null;
  category_code: string;
  category_name: string;
  status: string;
  received_on: string;
  days_waiting: number;
  application_fee_minor: string | null;
  fee_received_on: string | null;
  proposed_by: string | null;
  seconded_by: string | null;
  motion_id: string | null;
  motion_subject: string | null;
  meeting_held_on: string | null;
  motion_carried: boolean | null;
  outcome_note: string | null;
  decided_on: string | null;
  membership_id: string | null;
  waiting_on: string | null;
}

interface Category {
  id: string;
  code: string;
  name: string;
}

/** How worried to be about how long this one has been sitting. */
function waitTone(days: number, status: string): 'good' | 'warning' | 'critical' {
  if (['admitted', 'declined', 'withdrawn'].includes(status)) return 'good';
  if (days > 90) return 'critical';
  if (days > 45) return 'warning';
  return 'good';
}

export function ApplicationsPage() {
  const { currency, atLeast } = useSession();
  const canDecide = atLeast('manager');
  const [show, setShow] = useState<'open' | 'all'>('open');
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const list = useApi<{ applications: Application[] }>(
    `/v1/applications?status=${show}`,
    [show],
  );
  const rows = list.data?.applications ?? [];

  const sorted = useSort(
    rows,
    {
      applicant: (a) => a.applicant,
      category: (a) => a.category_name,
      received: (a) => a.received_on,
      waiting: (a) => a.days_waiting,
      status: (a) => a.status,
    },
    { key: 'waiting', dir: 'desc' },
  );

  const stuck = rows.filter(
    (a) => a.days_waiting > 45 && !['admitted', 'declined', 'withdrawn'].includes(a.status),
  );
  const ready = rows.filter((a) => a.status === 'approved');

  return (
    <>
      <div className="row" style={{ marginBottom: 16 }}>
        <div>
          <h1>Applications</h1>
          <div className="muted small">Who has asked to join, and what each one is waiting on</div>
        </div>
        <div className="spacer" />
        <select value={show} onChange={(e) => setShow(e.target.value as typeof show)}>
          <option value="open">Still open</option>
          <option value="all">Everything, including decided</option>
        </select>
        {!adding && (
          <button className="gold" onClick={() => setAdding(true)}>
            Take an application
          </button>
        )}
      </div>

      <ErrorBanner error={list.error} />
      {note && (
        <Card>
          <Status tone="good">{note}</Status>
        </Card>
      )}

      <div className="tiles">
        <Tile label="Open" value={rows.filter((a) => !a.decided_on).length} />
        <Tile
          label="Ready to admit"
          value={ready.length}
          note={ready.length > 0 ? 'the Board has said yes' : 'nothing approved'}
          tone={ready.length > 0 ? 'good' : 'neutral'}
        />
        <Tile
          label="Waiting over 45 days"
          value={stuck.length}
          note={stuck.length > 0 ? 'somebody should look at these' : 'nothing is stalled'}
          tone={stuck.length > 0 ? 'warning' : 'good'}
        />
      </div>

      {adding && (
        <TakeApplication
          onDone={(m) => {
            setAdding(false);
            setNote(m);
            list.reload();
          }}
          onCancel={() => setAdding(false)}
        />
      )}

      <Card flush>
        {list.loading ? (
          <div style={{ padding: 16 }}>
            <Loading rows={4} />
          </div>
        ) : rows.length === 0 ? (
          <Empty>
            {show === 'open'
              ? 'Nobody is waiting. An application taken at the desk appears here.'
              : 'No applications have been taken yet.'}
          </Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <SortTh s={sorted} col="applicant">Applicant</SortTh>
                <SortTh s={sorted} col="category">Category</SortTh>
                <SortTh s={sorted} col="received">Received</SortTh>
                <SortTh s={sorted} col="waiting">Waiting</SortTh>
                <SortTh s={sorted} col="status">Waiting on</SortTh>
                <th style={{ width: 110 }} />
              </tr>
            </thead>
            <tbody>
              {sorted.rows.map((a) => (
                <Fragment key={a.id}>
                  <tr>
                    <td>
                      <strong>{a.applicant}</strong>
                      <div className="muted small">
                        {[a.applicant_email, a.applicant_phone].filter(Boolean).join(' · ') ||
                          'no contact details'}
                      </div>
                    </td>
                    <td className="muted">{a.category_name}</td>
                    <td className="muted">{date(a.received_on)}</td>
                    <td>
                      <Status tone={waitTone(a.days_waiting, a.status)}>
                        {a.days_waiting} day{a.days_waiting === 1 ? '' : 's'}
                      </Status>
                    </td>
                    <td>
                      {a.waiting_on ?? (
                        <span className="muted">
                          {a.status}
                          {a.decided_on ? ` · ${date(a.decided_on)}` : ''}
                        </span>
                      )}
                    </td>
                    <td className="actions">
                      <button
                        className="secondary small"
                        aria-expanded={open === a.id}
                        onClick={() => setOpen(open === a.id ? null : a.id)}
                      >
                        {open === a.id ? 'Close' : 'Open'}
                      </button>
                    </td>
                  </tr>
                  {open === a.id && (
                    <tr>
                      <td colSpan={6} style={{ background: 'var(--surface-sunk)' }}>
                        <ApplicationDetail
                          application={a}
                          canDecide={canDecide}
                          currency={currency}
                          onChanged={(m) => {
                            setNote(m ?? null);
                            list.reload();
                          }}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

/* ------------------------------------------------------------------ */

function TakeApplication({
  onDone,
  onCancel,
}: {
  onDone: (message: string) => void;
  onCancel: () => void;
}) {
  const categories = useApi<{ categories: Category[] }>('/v1/membership-categories');
  const [f, setF] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    membership_category: '',
  });
  const [proposer, setProposer] = useState<PersonHit | null>(null);
  const [seconder, setSeconder] = useState<PersonHit | null>(null);

  const take = useMutation(async () =>
    api<{ applicant: string }>('/v1/applications', {
      method: 'POST',
      body: {
        applicant: {
          first_name: f.first_name.trim(),
          last_name: f.last_name.trim(),
          email: f.email.trim() || null,
          phone: f.phone.trim() || null,
        },
        membership_category: f.membership_category,
        proposed_by_person_id: proposer?.person_id ?? null,
        seconded_by_person_id: seconder?.person_id ?? null,
      },
    }),
  );

  const set = (k: keyof typeof f) => (v: string) => setF({ ...f, [k]: v });
  const cats = categories.data?.categories ?? [];
  const ready = f.first_name.trim() && f.last_name.trim() && f.membership_category;

  return (
    <Card title="Take an Application">
      <ErrorBanner error={take.error ?? categories.error} />

      <div className="hint" style={{ marginBottom: 12 }}>
        Enough to write it down. The proposer and the seconder can be added later — the Board
        will not see it until both are there, and the list says so — but an application that
        cannot be saved until it is complete is an application that stays on a desk.
      </div>

      <div className="grid-2">
        <label className="field">
          <span>First name</span>
          <input value={f.first_name} autoFocus onChange={(e) => set('first_name')(e.target.value)} />
        </label>
        <label className="field">
          <span>Last name</span>
          <input value={f.last_name} onChange={(e) => set('last_name')(e.target.value)} />
        </label>
        <label className="field">
          <span>Email</span>
          <input type="email" value={f.email} onChange={(e) => set('email')(e.target.value)} />
        </label>
        <label className="field">
          <span>Telephone</span>
          <input value={f.phone} onChange={(e) => set('phone')(e.target.value)} />
        </label>
        <label className="field">
          <span>Applying for</span>
          <select
            value={f.membership_category}
            onChange={(e) => set('membership_category')(e.target.value)}
          >
            <option value="">Choose a category…</option>
            {cats.map((c) => (
              <option key={c.id} value={c.code}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="grid-2">
        <PersonLookup
          label="Proposed by"
          hint="A member of the club. Not the applicant — the database refuses that."
          value={proposer}
          onChange={setProposer}
        />
        <PersonLookup
          label="Seconded by"
          hint="A different member from the proposer."
          value={seconder}
          onChange={setSeconder}
        />
      </div>

      <div className="actions" style={{ marginTop: 12 }}>
        <div className="spacer" />
        <button className="secondary" onClick={onCancel}>
          Cancel
        </button>
        <button
          className="gold"
          disabled={take.busy || !ready}
          onClick={async () => {
            const r = await take.run();
            if (r) onDone(`${r.applicant} has been added to the applications list.`);
          }}
        >
          {take.busy ? 'Saving…' : 'Save the application'}
        </button>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ */

interface Motion {
  id: string;
  subject: string;
  held_on: string;
  carried: boolean | null;
}

function ApplicationDetail({
  application: a,
  canDecide,
  currency,
  onChanged,
}: {
  application: Application;
  canDecide: boolean;
  currency: { code: string; symbol: string | null; exponent: number };
  onChanged: (message?: string) => void;
}) {
  const [proposer, setProposer] = useState<PersonHit | null>(null);
  const [seconder, setSeconder] = useState<PersonHit | null>(null);
  const [motionId, setMotionId] = useState(a.motion_id ?? '');
  const [reason, setReason] = useState('');

  // Carried motions the Board has actually passed. Only these can carry
  // an approval — MEM-21 refuses anything else — so offering the rest
  // would be offering a choice that comes back as a red banner.
  const motions = useApi<{ motions: Motion[] }>('/v1/governance/motions?carried=true');

  const save = useMutation(async (body: Record<string, unknown>) =>
    api(`/v1/applications/${a.id}`, { method: 'PATCH', body }),
  );
  const admit = useMutation(async () =>
    api<{ membership_number: string; account_number: string }>(
      `/v1/applications/${a.id}/admit`,
      { method: 'POST', body: {} },
    ),
  );

  const decided = Boolean(a.decided_on);

  return (
    <div style={{ padding: '10px 0 14px' }}>
      <ErrorBanner error={save.error ?? admit.error} />

      <dl className="receipt__facts" style={{ marginTop: 0 }}>
        <div>
          <dt>Received</dt>
          <dd>
            {date(a.received_on)} · {a.days_waiting} days ago
          </dd>
        </div>
        <div>
          <dt>Proposed by</dt>
          <dd>{a.proposed_by ?? <span className="muted">nobody yet</span>}</dd>
        </div>
        <div>
          <dt>Seconded by</dt>
          <dd>{a.seconded_by ?? <span className="muted">nobody yet</span>}</dd>
        </div>
        {a.application_fee_minor && (
          <div>
            <dt>Application fee</dt>
            <dd>
              {money(a.application_fee_minor, currency)}
              {a.fee_received_on ? ` · received ${date(a.fee_received_on)}` : ' · not received'}
            </dd>
          </div>
        )}
        {a.motion_subject && (
          <div>
            <dt>Board motion</dt>
            <dd>
              {a.motion_subject}
              {a.meeting_held_on && (
                <span className="muted small"> · {date(a.meeting_held_on)}</span>
              )}
              {a.motion_carried === false && (
                <span className="muted small"> · did not carry</span>
              )}
            </dd>
          </div>
        )}
        {a.outcome_note && (
          <div>
            <dt>Outcome</dt>
            <dd>{a.outcome_note}</dd>
          </div>
        )}
      </dl>

      {decided ? (
        <Status tone={a.status === 'admitted' ? 'good' : 'neutral'}>
          {a.status === 'admitted'
            ? `Admitted on ${date(a.decided_on!)}.`
            : `${a.status[0]!.toUpperCase()}${a.status.slice(1)} on ${date(a.decided_on!)}.`}
        </Status>
      ) : (
        <>
          {/* The sponsors, where they are still missing. Shown here
              rather than on a separate edit screen because "needs a
              seconder" is the single most common reason one of these
              stalls, and the fix should be in the same place as the
              complaint. */}
          {(!a.proposed_by || !a.seconded_by) && (
            <div className="grid-2">
              {!a.proposed_by && (
                <div>
                  <PersonLookup
                    label="Proposed by"
                    value={proposer}
                    onChange={async (p) => {
                      setProposer(p);
                      if (p) {
                        const done = await save.run({ proposed_by_person_id: p.person_id });
                        if (done) onChanged();
                      }
                    }}
                  />
                </div>
              )}
              {!a.seconded_by && (
                <div>
                  <PersonLookup
                    label="Seconded by"
                    value={seconder}
                    onChange={async (p) => {
                      setSeconder(p);
                      if (p) {
                        const done = await save.run({ seconded_by_person_id: p.person_id });
                        if (done) onChanged();
                      }
                    }}
                  />
                </div>
              )}
            </div>
          )}

          <div className="actions" style={{ marginTop: 10, flexWrap: 'wrap' }}>
            {a.status === 'draft' || a.status === 'submitted' ? (
              <button
                className="secondary"
                disabled={save.busy || !a.proposed_by || !a.seconded_by}
                title={
                  !a.proposed_by || !a.seconded_by
                    ? 'The Board will not see it without a proposer and a seconder'
                    : undefined
                }
                onClick={async () => {
                  const done = await save.run({ status: 'before_the_board' });
                  if (done) onChanged(`${a.applicant} is now before the Board.`);
                }}
              >
                Put before the Board
              </button>
            ) : null}

            {a.status === 'before_the_board' && canDecide && (
              <>
                <select value={motionId} onChange={(e) => setMotionId(e.target.value)}>
                  <option value="">Which motion carried it…</option>
                  {(motions.data?.motions ?? []).map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.subject} · {date(m.held_on)}
                    </option>
                  ))}
                </select>
                <button
                  className="gold"
                  disabled={save.busy || !motionId}
                  onClick={async () => {
                    const done = await save.run({ status: 'approved', motion_id: motionId });
                    if (done) onChanged(`${a.applicant} has been approved.`);
                  }}
                >
                  Approved by that motion
                </button>
              </>
            )}

            {a.status === 'approved' && canDecide && (
              <button
                className="gold"
                disabled={admit.busy}
                onClick={async () => {
                  const r = await admit.run();
                  if (r) {
                    onChanged(
                      `${a.applicant} is a member — membership ${r.membership_number}, ` +
                        `account ${r.account_number}.`,
                    );
                  }
                }}
              >
                {admit.busy ? 'Admitting…' : 'Admit as a member'}
              </button>
            )}

            <div className="spacer" />

            {canDecide && a.status !== 'approved' && (
              <>
                <input
                  value={reason}
                  placeholder="Why, if declining"
                  onChange={(e) => setReason(e.target.value)}
                  style={{ maxWidth: 260 }}
                />
                <button
                  className="secondary"
                  disabled={save.busy || reason.trim().length < 3}
                  title={
                    reason.trim().length < 3
                      ? 'A declined application needs a reason — the applicant will ask'
                      : undefined
                  }
                  onClick={async () => {
                    const done = await save.run({
                      status: 'declined',
                      outcome_note: reason.trim(),
                    });
                    if (done) onChanged(`${a.applicant}'s application was declined.`);
                  }}
                >
                  Decline
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
